import type { Instrument, PairListing } from '@nutrimatic/sdk'
import { NATIVE_TOKEN_ADDRESS } from '@sirius-cybernetics-42/product-config'

import { getActiveChainId } from '@/config/active'
import { buildPoolDetailPath } from '@/config/liquidityPools'
import { buildTokenDetailPath } from '@/config/tokenProfiles'
import { nativeCurrencyId } from '@/config/native'
import { poolAddressFromVenueId } from '@/data/nutrimatic/ids'
import { toSwapCurrencyId } from '@/features/swap/entry/swapCurrencyId'
import { buildSwapEntryUrl } from '@/features/swap/entry/swapEntryQuery'
import type { ChainId } from '@/sdk'

/** Resolve Nutrimatic instrument → Don't Panic swap currency id. */
export function instrumentToCurrencyId(
  instrument: Instrument | undefined,
  chainId: ChainId = getActiveChainId(),
): string | null {
  if (!instrument) return null
  if (!instrument.onChainAddress) {
    // Native gas asset (e.g. 42) — no on-chain address in Nutrimatic catalog.
    return nativeCurrencyId(chainId)
  }
  return toSwapCurrencyId(instrument.onChainAddress, chainId)
}

export function instrumentToTokenDetailPath(instrument: Instrument): string {
  if (!instrument.onChainAddress) {
    return buildTokenDetailPath(NATIVE_TOKEN_ADDRESS)
  }
  return buildTokenDetailPath(instrument.onChainAddress)
}

export function listingPoolAddress(
  listing: PairListing,
  venuesPoolAddress?: string | undefined,
): string | undefined {
  const fromVenue = venuesPoolAddress?.toLowerCase()
  const fromId = poolAddressFromVenueId(listing.venueId)
  return fromVenue || fromId || undefined
}

/** Trade CTA — pair address entry (operational path unchanged). */
export function buildTradeHrefFromListing(
  listing: PairListing,
  venuesPoolAddress?: string,
): string {
  const pairAddress = listingPoolAddress(listing, venuesPoolAddress)
  if (pairAddress) {
    return buildSwapEntryUrl({ type: 'pair', pairAddress })
  }
  return '/swap'
}

/** Pool detail deep link by pair address (resolvable by seed or Nutrimatic). */
export function buildPoolHrefFromListing(
  listing: PairListing,
  venuesPoolAddress?: string,
): string {
  const pairAddress = listingPoolAddress(listing, venuesPoolAddress)
  if (pairAddress) return buildPoolDetailPath(pairAddress)
  return buildPoolDetailPath(encodeURIComponent(listing.pairLabel))
}

export function buildAddLiquidityHref(
  base: Instrument | undefined,
  quote: Instrument | undefined,
  chainId: ChainId = getActiveChainId(),
): string {
  const a = instrumentToCurrencyId(base, chainId)
  const b = instrumentToCurrencyId(quote, chainId)
  if (!a || !b) return '/add'
  return `/add/${a}/${b}`
}
